import React, { useRef, useState } from 'react';
import { Card, Table, Row, Col, Button, Modal, Form, InputNumber, App, Select, Alert, Progress, Tag } from 'antd';
import { useOwnerKpiSnapshots, useOwnerDataQuality } from '@/hooks/useOwnerIntelligence';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  SignalBadge,
  ConfidenceDots,
  PrimaryKpiCard,
  OiPageFrame,
  OiDetailDrawer,
  OiKpiDrillDownPanel,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiDonutChart } from '@/components/owner-intelligence/charts/OiCharts';
import type { RagStatus } from '@/services/ownerIntelligence.service';

type KpiSnapshotRow = {
  kpi_code: string;
  kpi_name: string;
  dimension?: string | null;
  unit?: string;
  actual_value?: number | null;
  target_value?: number | null;
  variance_pct?: number | null;
  status?: RagStatus | null;
  confidence_score?: number;
  data_flag?: string;
};

const KpiPerformancePage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [periodType, setPeriodType] = useState('monthly');
  const [drillDown, setDrillDown] = useState<{ kpi_code: string; rows: unknown[] } | null>(null);
  const [narrative, setNarrative] = useState('');
  const { data: kpis, isLoading, error } = useOwnerKpiSnapshots(periodType);
  const { data: dq, isLoading: dqLoading } = useOwnerDataQuality();
  const { data: catalogData } = useQuery({
    queryKey: ['owner-intelligence', 'kpi-catalog'],
    queryFn: () => ownerIntelligenceService.listKpiCatalog(),
  });
  const [editKpi, setEditKpi] = useState<{ kpi_code: string; kpi_name: string; target_value: number | null } | null>(null);
  const [form] = Form.useForm();
  const [dqDismissed, setDqDismissed] = useState(false);
  const dqSectionRef = useRef<HTMLDivElement>(null);

  const periodKeyFor = (type: string) => {
    const now = new Date();
    if (type === 'yearly') return `${now.getFullYear()}`;
    if (type === 'quarterly') return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const renderKpiStatus = (row: KpiSnapshotRow) => {
    if (row.actual_value == null) return <Tag>Not yet tracked</Tag>;
    if (row.target_value == null) return <Tag color="default">No target set</Tag>;
    if (!row.status) return <Tag color="orange">Incomplete</Tag>;
    return <SignalBadge tier={row.status === 'green' ? 'healthy' : row.status === 'amber' ? 'watch' : 'at_risk'} />;
  };

  const formatValue = (v: number | null | undefined, unit?: string | null) => {
    if (v == null) return '—';
    if (unit === 'currency') return formatCurrency(v);
    if (unit === 'percent') return `${v.toFixed(1)}%`;
    return String(v);
  };

  const saveTarget = async () => {
    if (!editKpi) return;
    try {
      const values = await form.validateFields();
      await ownerIntelligenceService.upsertKpiTarget({
        kpi_code: editKpi.kpi_code,
        period_type: periodType,
        period_key: periodKeyFor(periodType),
        target_value: values.target_value,
      });
      message.success('Target saved', 4);
      setEditKpi(null);
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'kpi-snapshots'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const openDrillDown = async (kpiCode: string, variancePct: number | null, actual: number | null) => {
    const [drill, narr] = await Promise.all([
      ownerIntelligenceService.getKpiDrillDown(kpiCode),
      ownerIntelligenceService.getNarrative({ kpi_code: kpiCode, variance_pct: variancePct ?? undefined, actual: actual ?? undefined }),
    ]);
    setDrillDown(drill as { kpi_code: string; rows: unknown[] });
    setNarrative((narr as { commentary?: string })?.commentary ?? '');
  };

  const kpiRows = (kpis as KpiSnapshotRow[]) ?? [];
  const issues = (dq as { score?: number; issues?: Array<{ issue_code: string; description: string; severity: string; affected_count: number; issue_category: string }> })?.issues ?? [];
  const onTarget = kpiRows.filter((k) => k.status === 'green').length;
  const offTarget = kpiRows.length - onTarget;

  return (
    <OiPageFrame
      subtitle="KPI catalog, variance tracking, and data quality for executive reporting."
      loading={isLoading && !kpis}
      error={Boolean(error)}
      errorMessage="KPI performance data unavailable"
    >
      {!dqDismissed && issues.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setDqDismissed(true)}
          message={`${issues[0].affected_count} records affected — ${issues[0].description}`}
          action={
            <Button
              size="small"
              onClick={() => {
                setDqDismissed(true);
                dqSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Fix now
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <PrimaryKpiCard label="KPIs tracked" value={kpiRows.length} />
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} title="On target">
            <OiDonutChart
              data={[
                { name: 'On target', value: onTarget },
                { name: 'Off target', value: offTarget },
              ]}
            />
            <div className="oi-mono-value" style={{ textAlign: 'center' }}>
              {onTarget} / {kpiRows.length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <PrimaryKpiCard
            label="Data quality score"
            value={dqLoading ? '…' : `${(dq as { score?: number })?.score ?? '—'}%`}
          />
        </Col>
      </Row>

      <Card
        title="KPI variance"
        bordered={false}
        extra={
          <Select
            value={periodType}
            onChange={setPeriodType}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
        }
        style={{ marginBottom: 16 }}
      >
        <Table<KpiSnapshotRow>
          size="small"
          rowKey="kpi_code"
          dataSource={kpiRows}
          onRow={(row) => ({
            onClick: () => openDrillDown(row.kpi_code, row.variance_pct ?? null, row.actual_value ?? null),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Dimension', dataIndex: 'dimension', render: (v: string) => v ?? '—' },
            { title: 'KPI', dataIndex: 'kpi_name' },
            {
              title: 'Actual vs target',
              render: (_, row) => {
                const actual = Number(row.actual_value ?? 0);
                const target = Number(row.target_value ?? 0);
                const pct = target > 0 ? Math.min(100, (actual / target) * 100) : 0;
                return <Progress percent={Math.round(pct)} size="small" showInfo={false} />;
              },
            },
            {
              title: 'Actual',
              dataIndex: 'actual_value',
              render: (v: number, row) => formatValue(v, row.unit),
            },
            {
              title: 'Target',
              dataIndex: 'target_value',
              render: (v: number | null, row) =>
                v == null ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditKpi({ kpi_code: row.kpi_code, kpi_name: row.kpi_name, target_value: null });
                      form.setFieldsValue({ target_value: undefined });
                    }}
                  >
                    Set target
                  </Button>
                ) : (
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditKpi({ kpi_code: row.kpi_code, kpi_name: row.kpi_name, target_value: v });
                      form.setFieldsValue({ target_value: v });
                    }}
                  >
                    {formatValue(v, row.unit)}
                  </Button>
                ),
            },
            {
              title: 'Variance %',
              dataIndex: 'variance_pct',
              render: (v: number) => (v != null ? `${v.toFixed(1)}%` : '—'),
            },
            { title: 'Status', key: 'status', render: (_: unknown, row: KpiSnapshotRow) => renderKpiStatus(row) },
            {
              title: 'Data coverage',
              dataIndex: 'confidence_score',
              render: (v: number, row: KpiSnapshotRow) =>
                v != null ? <ConfidenceDots value={v} label={`${row.data_flag === 'incomplete' ? 'Incomplete data' : 'ERP data present'}`} /> : '—',
            },
          ]}
        />
      </Card>

      <Card title="Data quality issues" style={{ marginBottom: 16 }} bordered={false} ref={dqSectionRef}>
        <Table
          size="small"
          rowKey="issue_code"
          dataSource={issues}
          locale={{ emptyText: 'No data quality issues detected' }}
          columns={[
            { title: 'Category', dataIndex: 'issue_category' },
            { title: 'Issue', dataIndex: 'description' },
            { title: 'Severity', dataIndex: 'severity' },
            { title: 'Affected', dataIndex: 'affected_count' },
          ]}
        />
      </Card>

      {(catalogData as { catalog?: unknown[] })?.catalog ? (
        <Card title="KPI catalog" bordered={false}>
          <Table
            size="small"
            rowKey="kpi_code"
            pagination={{ pageSize: 10 }}
            dataSource={(catalogData as { catalog: unknown[] }).catalog as Record<string, unknown>[]}
            columns={[
              { title: 'Code', dataIndex: 'kpi_code' },
              { title: 'Name', dataIndex: 'kpi_name' },
              { title: 'Dimension', dataIndex: 'dimension' },
              { title: 'Unit', dataIndex: 'unit' },
            ]}
          />
        </Card>
      ) : null}

      <Modal title={`Set target — ${editKpi?.kpi_name ?? ''}`} open={!!editKpi} onCancel={() => setEditKpi(null)} onOk={saveTarget}>
        <Form form={form} layout="vertical">
          <Form.Item name="target_value" label="Monthly target" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <OiDetailDrawer
        open={Boolean(drillDown)}
        onClose={() => setDrillDown(null)}
        title={`KPI drill-down — ${drillDown?.kpi_code ?? ''}`}
        subtitle={narrative}
      >
        {drillDown ? <OiKpiDrillDownPanel kpiCode={drillDown.kpi_code} rows={drillDown.rows} /> : null}
      </OiDetailDrawer>
    </OiPageFrame>
  );
};

export default KpiPerformancePage;
